import { useState } from 'react'
import { useParams } from 'react-router'
import { Grid } from '@material-ui/core'
import AddRoundedIcon from '@material-ui/icons/AddRounded'
import { useTranslation } from 'react-i18next'

import {
  useAddOwnKnowledgeBaseGroupMutation,
  useAddKnowledgeBaseIdGroupMutation,
  useAddOwnKnowledgeBaseObjectMutation,
  useAddIdKnowledgeBaseObjectMutation,
  useAddIdKnowledgeBaseFilesMutation,
  useAddOwnKnowledgeBaseFilesMutation,
} from 'api/mutations/knowledgeBase/knowledgeBase'
import { useCompanyId } from 'providers/CompanyProvider'
import { RestrictedContent } from 'components/auth/RestrictedContent/RestrictedContent'
import { GroupModal } from 'components/modals/GroupModal/GroupModal'
import { ADMIN_ROLE, COMPANY_ROLE, CONTENT_CREATOR_ROLE } from 'constants/roleGroups'
import * as Styled from 'screens/KnowledgeBase/KnowledgeBaseScreen/KnowledgeBaseScreen.styles'
import { parseGroupFields } from 'screens/KnowledgeBase/KnowledgeBaseScreen/KnowledgeBaseScreen.utils'
import { AddFilesModal } from 'components/modals/AddFilesModal/AddFilesModal'
import { KnowledgeBaseContentFilesFormFields } from 'components/forms/KnowledgeBaseContentFilesForm/types'
import { useKnowledgeBaseFormImage } from 'hooks/useKnowledgeBaseFormImage'
import { GroupFormFields } from 'components/forms/GroupForm/types'
import { KnowledgeBaseContentModal } from 'components/modals/KnowledgeBaseContentModal/KnowledgeBaseContentModal'
import { useGetKnowledgeBaseEntityQuery } from 'api/queries/knowledgeBase/knowledgeBase'
import { useDestination } from 'hooks/useDestination'
import { parseContentFields } from 'components/forms/KnowledgeBaseContentForm/utils'
import { KnowledgeBaseContentFormFields } from 'components/forms/KnowledgeBaseContentForm/KnowledgeBaseContentForm'
import { useHasRole } from 'hooks/useHasRoles'

interface ParamTypes {
  id: string
}

interface KnowledgeBaseCreateSectionProps {
  onSuccess: (() => void) | (() => Promise<void>)
  kbEntityId?: number
}

const KnowledgeBaseCreateSection = ({
  onSuccess,
  kbEntityId,
}: KnowledgeBaseCreateSectionProps) => {
  const { id } = useParams<ParamTypes>()
  const { t } = useTranslation()
  const companyId = useCompanyId() || 0
  const isAdmin = useHasRole(ADMIN_ROLE)

  const [isAddGroupModalOpen, setAddGroupModalOpen] = useState<boolean>(false)
  const [isAddFilesModalOpen, setAddFilesModalOpen] = useState<boolean>(false)
  const [filesUploadProgress, setFilesUploadProgress] = useState<number>(0)
  const [
    isAddKnowledgeBaseContentModalOpen,
    setAddKnowledgeBaseContentModalOpen
  ] = useState<boolean>(false)

  const handleAddGroupButton = () => setAddGroupModalOpen(true)

  const handleAddContentButton = () => setAddKnowledgeBaseContentModalOpen(true)

  const { data: knowledgeBaseData } = useGetKnowledgeBaseEntityQuery(+id, { enabled: !!id })

  const handleAddFilesButton = () => setAddFilesModalOpen(true)

  const {
    mutate: addOwnGroup,
    isLoading: isAddOwnGroupLoading
  } = useAddOwnKnowledgeBaseGroupMutation()

  const {
    mutate: addIdGroup,
    isLoading: isAddIdGroupLoading
  } = useAddKnowledgeBaseIdGroupMutation()

  const {
    isAddImageMutationLoading,
    isDeleteImageMutationLoading,
    handleSubmitImage
  } = useKnowledgeBaseFormImage({
    groupId: +id || undefined,
  })

  const {
    mutate: addOwnFiles,
    isLoading: isAddOwnFilesLoading
  } = useAddOwnKnowledgeBaseFilesMutation({
    onSuccess: async () => {
      await onSuccess()
      setAddFilesModalOpen(false)
      setFilesUploadProgress(0)
    },
    onError: () => {
      setFilesUploadProgress(0)
    }
  },
  setFilesUploadProgress)

  const {
    mutate: addIdFiles,
    isLoading: isAddIdFilesLoading
  } = useAddIdKnowledgeBaseFilesMutation(companyId, {
    onSuccess: async () => {
      await onSuccess()
      setAddFilesModalOpen(false)
      setFilesUploadProgress(0)
    },
    onError: () => {
      setFilesUploadProgress(0)
    }
  },
  setFilesUploadProgress)

  const {
    mutate: addOwnObjectMutate,
    isLoading: isCreateOwnKnowledgeBaseContentLoading,
  } = useAddOwnKnowledgeBaseObjectMutation()

  const {
    mutate: addIdObjectMutate,
    isLoading: isCreateIdKnowledgeBaseContentLoading,
  } = useAddIdKnowledgeBaseObjectMutation()

  const destinationText = useDestination(knowledgeBaseData, { includeItself: true })

  const addOwnKnowledgeBaseContent = async (
    variables: KnowledgeBaseContentFormFields,
    languageCode: string
  ) => {
    const { image } = variables
    const values = parseContentFields(variables, languageCode, isAdmin)

    await addOwnObjectMutate(
      { ...values },
      {
        onSuccess: async (data) => {
          await handleSubmitImage(image, data.id)
          await onSuccess()
          setAddKnowledgeBaseContentModalOpen(false)
        },
      }
    )
  }

  const isAddFilesLoading = companyId ? isAddIdFilesLoading : isAddOwnFilesLoading

  const addIdKnowledgeBaseContent = async (
    variables: KnowledgeBaseContentFormFields,
    languageCode: string
  ) => {
    const { image } = variables
    const values = parseContentFields(variables, languageCode, isAdmin)

    await addIdObjectMutate(
      { companyId, ...values },
      {
        onSuccess: async (data) => {
          await handleSubmitImage(image, data.id)
          await onSuccess()
          setAddKnowledgeBaseContentModalOpen(false)
        },
      }
    )
  }

  const handleSaveAddOwnGroup = (values: GroupFormFields) => {
    const group = parseGroupFields(
      values.name,
      kbEntityId || null,
      values.nameTranslations,
      values.labelIds,
    )
    addOwnGroup(group, {
      onSuccess: async ({ data }) => {
        await handleSubmitImage(values.image, data.id)
        await onSuccess()
        setAddGroupModalOpen(false)
      }
    })
  }

  const handleSaveAddIdGroup = (values: GroupFormFields) => {
    const group = parseGroupFields(
      values.name,
      kbEntityId || null,
      values.nameTranslations,
      values.labelIds,
    )
    addIdGroup({
      companyId,
      ...group
    }, {
      onSuccess: async ({ data }) => {
        await handleSubmitImage(values.image, data.id)
        await onSuccess()
        setAddGroupModalOpen(false)
      }
    })
  }

  const handleSaveAddFiles = (values: KnowledgeBaseContentFilesFormFields) => {
    if (isAddFilesLoading) {
      return
    }
    if (companyId) {
      addIdFiles({
        files: values.files,
        parentId: kbEntityId,
        visibility: values.visibility
      })
    } else {
      addOwnFiles({
        files: values.files,
        parentId: kbEntityId,
        visibility: values.visibility
      })
    }
  }

  return (
    <>
      <RestrictedContent accessRoles={[CONTENT_CREATOR_ROLE, COMPANY_ROLE, ADMIN_ROLE]}>
        <Grid item>
          <Styled.Button
            backgroundGradient
            onClick={handleAddGroupButton}
            endIcon={<AddRoundedIcon />}
          >
            {t('knowledgeBase.newGroup')}
          </Styled.Button>
          <Styled.Button
            backgroundGradient
            onClick={handleAddContentButton}
            endIcon={<AddRoundedIcon />}
          >
            {t('knowledgeBase.createNew')}
          </Styled.Button>
          <Styled.Button
            backgroundGradient
            onClick={handleAddFilesButton}
            endIcon={<AddRoundedIcon />}
          >
            {t('knowledgeBase.newFiles')}
          </Styled.Button>
        </Grid>
      </RestrictedContent>
      <GroupModal
        open={isAddGroupModalOpen}
        onClose={() => setAddGroupModalOpen(false)}
        onSave={companyId ? handleSaveAddIdGroup : handleSaveAddOwnGroup}
        isLoading={
          (companyId ? isAddIdGroupLoading : isAddOwnGroupLoading)
          || isAddImageMutationLoading
          || isDeleteImageMutationLoading
        }
      />
      <KnowledgeBaseContentModal
        open={isAddKnowledgeBaseContentModalOpen}
        onClose={() => setAddKnowledgeBaseContentModalOpen(false)}
        onSave={companyId ? addIdKnowledgeBaseContent : addOwnKnowledgeBaseContent}
        destinationPlaceholder={destinationText}
        initialDestination={knowledgeBaseData?.id}
        isLoading={
          (companyId
            ? isCreateIdKnowledgeBaseContentLoading
            : isCreateOwnKnowledgeBaseContentLoading)
          || isAddImageMutationLoading
          || isDeleteImageMutationLoading
        }
      />
      <AddFilesModal
        open={isAddFilesModalOpen}
        onClose={() => !isAddFilesLoading && setAddFilesModalOpen(false)}
        onSave={handleSaveAddFiles}
        isLoading={isAddFilesLoading}
        uploadProgress={filesUploadProgress}
      />
    </>
  )
}

export { KnowledgeBaseCreateSection }
